import { Alert, Box, Spinner } from '@zillow/constellation-10';
import { ZillowMapLayer } from '@zillow/zillow-map-control';
import { GeoJsonLayer } from './GeoJsonLayer';
import { Map } from './Map';
import {
  type MainLayoutContextValue,
  MainLayoutType,
  useMainLayout,
} from '../../layouts/MainPageLayout/MainLayoutContext';
import { useMapAccumulatedDots } from './useMapAccumulatedDots';
import { NumberedDot } from './NumberedDot';
import { DisplayingRegionLevel } from '../../../types/DisplayingRegionLevel';
import type { PortfolioAnalyticsPropertyResponseDto } from '@zg-rentals/ts-customer-analytics-client';

export const getFilterKeyFromDisplayingRegionLevel = (
  regionIdType: DisplayingRegionLevel,
): keyof MainLayoutContextValue['selectedFilters'] => {
  switch (regionIdType) {
    case DisplayingRegionLevel.STATE:
      return 'stateRegionId';
    case DisplayingRegionLevel.METRO_AREA:
      return 'metropolitanServiceAreaRegionId';
    case DisplayingRegionLevel.CITY:
      return 'cityRegionId';
    case DisplayingRegionLevel.ZIP:
      return 'zipCodeRegionId';
    default:
      return 'zipCodeRegionId';
  }
};

export const MapWithGeoJson = ({
  children,
  renderDotNumber = (_, count) => count,
  loading = false,
}: {
  children?: React.ReactNode;
  renderDotNumber?: (regionId: number, count: number) => number | string;
  loading?: boolean;
}) => {
  const {
    selectedRegionId,
    center,
    setCenter,
    bounds,
    setBounds,
    zoom,
    setZoom,
    geojson,
    isLoadingGeojson,
    errorGeojson,
    onSelectState,
    onSelectMetropolitanServiceArea,
    onSelectCity,
    onSelectZIPCode,
    layout,
    setShouldForceBoundsToGeojsonBounds,
  } = useMainLayout();

  const dots = useMapAccumulatedDots();

  const finalLoading = loading || isLoadingGeojson;

  if (layout !== MainLayoutType.MAP) {
    return null;
  }

  return (
    <Box
      css={{
        height: '100%',
        position: 'relative',
        marginInline: '-800',
        marginBlockEnd: '-800',
      }}
    >
      <Map center={center} zoom={zoom} bounds={bounds} setCenter={setCenter} setZoom={setZoom} setBounds={setBounds}>
        <ZillowMapLayer>
          {geojson && (
            <GeoJsonLayer
              data={geojson}
              onFeatureClick={(feature) => {
                const geom = feature.getGeometry();
                const type = geom && geom.getType();
                if (type === 'Polygon' && !selectedRegionId) {
                  const regionId = Number(feature.getProperty('regionId'));
                  const regionIdType = feature.getProperty('regionIdType') as DisplayingRegionLevel;
                  const property = feature.getProperty('property') as PortfolioAnalyticsPropertyResponseDto;

                  if (regionIdType) {
                    switch (regionIdType) {
                      case DisplayingRegionLevel.STATE:
                        onSelectState(regionId);
                        break;
                      case DisplayingRegionLevel.METRO_AREA:
                        onSelectMetropolitanServiceArea(regionId, property.propertyDetails.stateRegionId);
                        break;
                      case DisplayingRegionLevel.CITY:
                        onSelectCity(
                          regionId,
                          property.propertyDetails.metroStatRegionId,
                          property.propertyDetails.stateRegionId,
                        );
                        break;
                      case DisplayingRegionLevel.ZIP:
                        onSelectZIPCode(
                          regionId,
                          property.propertyDetails.cityRegionId,
                          property.propertyDetails.metroStatRegionId,
                          property.propertyDetails.stateRegionId,
                        );
                        break;
                      default:
                        break;
                    }
                  }
                }
              }}
            />
          )}
        </ZillowMapLayer>
        <ZillowMapLayer>
          {dots.map((dot) => (
            <NumberedDot
              key={dot.lat + dot.lng}
              lat={dot.lat}
              lng={dot.lng}
              label={renderDotNumber(dot.regionId, dot.count)}
              onClick={() => {
                const latOffset = 0.9;
                const lngOffset = 0.9;
                setShouldForceBoundsToGeojsonBounds(false);

                setBounds({
                  northLat: dot.lat + latOffset,
                  southLat: dot.lat - latOffset,
                  eastLng: dot.lng + lngOffset,
                  westLng: dot.lng - lngOffset,
                });
              }}
            />
          ))}
        </ZillowMapLayer>
        {children}
      </Map>
      {(errorGeojson || finalLoading) && (
        <Box
          css={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {finalLoading && <Spinner size="lg" onImpact />}
          {errorGeojson && <Alert tone="critical">Unexpected error loading map</Alert>}
        </Box>
      )}
    </Box>
  );
};
